import {
  authoringDocSchema,
  type AuthoringBlockNode,
  EDITOR_CLIPBOARD_KIND,
  EDITOR_CLIPBOARD_VERSION,
  type EditorClipboardEnvelope,
} from '@personalizehq/schemas';
import type { Theme } from '@personalizehq/schemas/ir';
import { findStep, type AuthoringDoc } from './doc.js';
import { cloneSubtreesWithFreshIds, collectIds, findNode, insertNode, normalizeTopLevelSelection } from './tree.js';

export interface ClipboardAsset {
  id: string;
  url: string;
}

export interface CaptureClipboardInput {
  root: AuthoringBlockNode;
  ids: readonly string[];
  labels: AuthoringDoc['labels'];
  theme: Theme;
  source: EditorClipboardEnvelope['source'];
  assets?: readonly ClipboardAsset[];
  copiedAt?: number;
}

export interface PasteSummary {
  importedTokens: string[];
  renamedTokens: Array<{ from: string; to: string }>;
  repairs: string[];
}

export type PastePlan =
  | { ok: false; reasons: string[] }
  | {
      ok: true;
      roots: AuthoringBlockNode[];
      labels: Record<string, string>;
      theme: Theme;
      summary: PasteSummary;
    };

export function captureClipboardEnvelope(input: CaptureClipboardInput): EditorClipboardEnvelope | null {
  const ids = normalizeTopLevelSelection(input.root, input.ids).filter((id) => id !== input.root.id);
  if (ids.length === 0) return null;
  const roots = ids.map((id) => findNode(input.root, id)!.node);
  const detached = JSON.parse(JSON.stringify(roots)) as AuthoringBlockNode[];
  const dependencies = collectDependencies(detached, input.theme);
  const assetByUrl = new Map((input.assets ?? []).map((asset) => [asset.url, asset]));
  const assetById = new Map((input.assets ?? []).map((asset) => [asset.id, asset]));
  const referencedAssets = new Map<string, ClipboardAsset>();

  for (const url of dependencies.mediaUrls) {
    const asset = assetByUrl.get(url);
    if (asset) referencedAssets.set(asset.id, asset);
  }
  for (const id of dependencies.assetIds) {
    const asset = assetById.get(id);
    referencedAssets.set(id, asset ?? { id, url: '' });
  }
  for (const font of Object.values(dependencies.theme.fonts)) {
    if (!font.src) continue;
    const asset = assetById.get(font.src);
    referencedAssets.set(font.src, asset ?? { id: font.src, url: '' });
  }

  const labels: Record<string, string> = {};
  const copiedIds = new Set(detached.flatMap((root) => [...collectIds(root)]));
  for (const [id, label] of Object.entries(input.labels ?? {})) {
    if (copiedIds.has(id)) labels[id] = label;
  }

  return {
    kind: EDITOR_CLIPBOARD_KIND,
    version: EDITOR_CLIPBOARD_VERSION,
    source: input.source,
    copiedAt: input.copiedAt ?? Date.now(),
    roots: detached,
    labels,
    theme: dependencies.theme,
    assets: [...referencedAssets.values()],
    campaignIds: [...dependencies.campaignIds],
  };
}

export function planClipboardPaste({
  envelope,
  target,
  appId,
  stepId,
  parentId,
  index,
  availableAssetIds,
  availableCampaignIds,
}: {
  envelope: EditorClipboardEnvelope;
  target: AuthoringDoc;
  appId: string;
  stepId: string;
  parentId: string;
  index: number;
  availableAssetIds: ReadonlySet<string>;
  availableCampaignIds: ReadonlySet<string>;
}): PastePlan {
  const reasons: string[] = [];
  if (envelope.source.appId !== appId) reasons.push('Copied components belong to a different app.');
  for (const asset of envelope.assets) {
    if (!availableAssetIds.has(asset.id)) reasons.push(`Required asset “${asset.id}” is unavailable in this app.`);
  }
  for (const campaignId of envelope.campaignIds) {
    if (!availableCampaignIds.has(campaignId)) reasons.push(`Referenced campaign “${campaignId}” no longer exists.`);
  }
  // Doc-wide: the paste target may be a step inside a variant (ADR 0015).
  const step = findStep(target, stepId);
  if (!step) reasons.push('The target step no longer exists.');
  if (step && !findNode(step.root, parentId)) reasons.push('The paste destination no longer exists.');
  if (reasons.length > 0 || !step) return { ok: false, reasons };

  const nextTheme = JSON.parse(JSON.stringify(target.theme)) as Theme;
  const summary: PasteSummary = { importedTokens: [], renamedTokens: [], repairs: [] };
  const colorMap = mergeTokenFamily('colors', envelope.theme.colors, nextTheme.colors, summary);
  const fontMap = mergeTokenFamily('fonts', envelope.theme.fonts, nextTheme.fonts, summary);
  const radiusMap = mergeTokenFamily('radii', envelope.theme.radii, nextTheme.radii, summary);
  // Elevation/gradient tokens carry colour tokens, so remap those through colorMap before merging.
  const incomingElevations = Object.fromEntries(
    Object.entries(envelope.theme.elevations).map(([name, value]) => [
      name,
      { ...value, color: colorMap.get(value.color) ?? value.color },
    ])
  );
  const elevationMap = mergeTokenFamily('elevations', incomingElevations, nextTheme.elevations, summary);
  const incomingGradients = Object.fromEntries(
    Object.entries(envelope.theme.gradients).map(([name, value]) => [
      name,
      { ...value, stops: value.stops.map((stop) => colorMap.get(stop) ?? stop) },
    ])
  );
  const gradientMap = mergeTokenFamily('gradients', incomingGradients, nextTheme.gradients, summary);

  // A button variant COMPOSES the foundation tokens above, so remap every reference it holds through
  // the maps just built (ADR 0014) — colour/gradient bg, radii radius, elevations elevation.
  const incomingButtons = Object.fromEntries(
    Object.entries(envelope.theme.buttons).map(([name, value]) => [
      name,
      {
        ...value,
        bg:
          value.bg === null
            ? null
            : typeof value.bg === 'string'
              ? colorMap.get(value.bg) ?? value.bg
              : { gradient: gradientMap.get(value.bg.gradient) ?? value.bg.gradient },
        fg: colorMap.get(value.fg) ?? value.fg,
        radius: typeof value.radius === 'string' ? radiusMap.get(value.radius) ?? value.radius : value.radius,
        ...(value.elevation ? { elevation: elevationMap.get(value.elevation) ?? value.elevation } : {}),
      },
    ])
  );
  const buttonMap = mergeTokenFamily('buttons', incomingButtons, nextTheme.buttons, summary);

  const { clones, idMap } = cloneSubtreesWithFreshIds(envelope.roots);
  const remappedIds = new Set(idMap.values());
  const rewrite = (node: AuthoringBlockNode): void => {
    rewriteNodeTokens(node, colorMap, fontMap, buttonMap, radiusMap, elevationMap, gradientMap);
    if (node.block === 'progress' && node.tracks && !idMap.has(node.tracks) && !remappedIds.has(node.tracks)) {
      summary.repairs.push(`Cleared external carousel reference from “${node.id}”.`);
      delete node.tracks;
    }
    if ('children' in node) node.children.forEach(rewrite);
    if (node.block === 'repeat') rewrite(node.template);
    if (node.fallback) rewrite(node.fallback);
  };
  clones.forEach(rewrite);

  const labels: Record<string, string> = {};
  for (const [oldId, label] of Object.entries(envelope.labels)) {
    const newId = idMap.get(oldId);
    if (newId) labels[newId] = label;
  }

  const candidate = JSON.parse(JSON.stringify(target)) as AuthoringDoc;
  candidate.theme = nextTheme;
  const candidateStep = findStep(candidate, stepId)!;
  clones.forEach((root, offset) => insertNode(candidateStep.root, parentId, index + offset, root));
  candidate.labels = { ...(candidate.labels ?? {}), ...labels };
  const parsed = authoringDocSchema.safeParse(candidate);
  if (!parsed.success) {
    return {
      ok: false,
      reasons: parsed.error.issues.slice(0, 5).map((issue) => `${issue.path.join('.') || 'document'}: ${issue.message}`),
    };
  }

  return { ok: true, roots: clones, labels, theme: nextTheme, summary };
}

export function applyClipboardPaste(
  draft: AuthoringDoc,
  stepId: string,
  parentId: string,
  index: number,
  plan: Extract<PastePlan, { ok: true }>
): void {
  draft.theme = plan.theme;
  const step = findStep(draft, stepId);
  if (!step) throw new Error('paste: target step disappeared');
  plan.roots.forEach((root, offset) => insertNode(step.root, parentId, index + offset, root));
  draft.labels = { ...(draft.labels ?? {}), ...plan.labels };
}

function mergeTokenFamily<T>(
  family: keyof Theme,
  incoming: Record<string, T>,
  target: Record<string, T>,
  summary: PasteSummary
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [name, value] of Object.entries(incoming)) {
    if (!(name in target)) {
      target[name] = JSON.parse(JSON.stringify(value)) as T;
      map.set(name, name);
      summary.importedTokens.push(`${family}.${name}`);
      continue;
    }
    if (JSON.stringify(target[name]) === JSON.stringify(value)) {
      map.set(name, name);
      continue;
    }
    let suffix = 1;
    let next = `${name}.copy`;
    while (next in target) next = `${name}.copy${++suffix}`;
    target[next] = JSON.parse(JSON.stringify(value)) as T;
    map.set(name, next);
    summary.renamedTokens.push({ from: `${family}.${name}`, to: `${family}.${next}` });
  }
  return map;
}

function collectDependencies(roots: readonly AuthoringBlockNode[], theme: Theme): {
  theme: Theme;
  mediaUrls: Set<string>;
  assetIds: Set<string>;
  campaignIds: Set<string>;
} {
  const colors = new Set<string>();
  const fonts = new Set<string>();
  const buttons = new Set<string>();
  const radii = new Set<string>();
  const elevations = new Set<string>();
  const gradients = new Set<string>();
  const mediaUrls = new Set<string>();
  const assetIds = new Set<string>();
  const campaignIds = new Set<string>();
  const color = (value: string | undefined | null): void => { if (value) colors.add(value); };

  const walk = (node: AuthoringBlockNode): void => {
    const bg = node.style?.bg;
    if (typeof bg === 'string') color(bg);
    else if (bg && 'gradient' in bg) gradients.add(bg.gradient);
    else if (bg) bg.stops.forEach(color);
    color(node.style?.border?.color);
    const shadow = node.style?.shadow;
    if (typeof shadow === 'string') elevations.add(shadow);
    else if (shadow) color(shadow.color);
    if (typeof node.style?.radius === 'string') radii.add(node.style.radius);
    node.actions?.forEach((action) => {
      if (action.type === 'launch_experience') campaignIds.add(action.campaign_id);
    });
    switch (node.block) {
      case 'text': fonts.add(node.font); color(node.color); break;
      case 'button': fonts.add(node.font); buttons.add(node.variant); break;
      case 'image':
        if (node.source.kind === 'asset') {
          assetIds.add(node.source.asset_id);
        } else {
          mediaUrls.add(node.source.url);
        }
        color(node.tint);
        if (typeof node.radius === 'string') radii.add(node.radius);
        break;
      case 'video':
        if (node.source.kind === 'asset') {
          assetIds.add(node.source.asset_id);
        } else {
          mediaUrls.add(node.source.url);
        }
        if (node.poster) mediaUrls.add(node.poster);
        if (typeof node.radius === 'string') radii.add(node.radius);
        break;
      case 'progress': color(node.fill_color); color(node.track_color); break;
      default: break;
    }
    if ('children' in node) node.children.forEach(walk);
    if (node.block === 'repeat') walk(node.template);
    if (node.fallback) walk(node.fallback);
  };
  roots.forEach(walk);
  // A button variant composes foundation tokens (ADR 0014) — pull each into the right closure so a
  // pasted button keeps its gradient bg, radii radius, and elevation.
  for (const name of buttons) {
    const button = theme.buttons[name];
    if (!button) continue;
    color(button.fg);
    if (typeof button.bg === 'string') color(button.bg);
    else if (button.bg) gradients.add(button.bg.gradient);
    if (typeof button.radius === 'string') radii.add(button.radius);
    if (button.elevation) elevations.add(button.elevation);
  }
  // Elevation/gradient tokens carry colours INSIDE them, so pull those into the colour closure too
  // (ADR 0014) — otherwise a pasted card referencing `elevation.card` loses its shadow colour.
  for (const name of elevations) {
    const elevation = theme.elevations[name];
    if (elevation) color(elevation.color);
  }
  for (const name of gradients) {
    const gradient = theme.gradients[name];
    if (gradient) gradient.stops.forEach(color);
  }

  return {
    theme: {
      colors: Object.fromEntries([...colors].flatMap((name) => theme.colors[name] ? [[name, theme.colors[name]]] : [])),
      fonts: Object.fromEntries([...fonts].flatMap((name) => theme.fonts[name] ? [[name, theme.fonts[name]]] : [])),
      buttons: Object.fromEntries([...buttons].flatMap((name) => theme.buttons[name] ? [[name, theme.buttons[name]]] : [])),
      radii: Object.fromEntries([...radii].flatMap((name) => theme.radii[name] ? [[name, theme.radii[name]]] : [])),
      elevations: Object.fromEntries([...elevations].flatMap((name) => theme.elevations[name] ? [[name, theme.elevations[name]]] : [])),
      gradients: Object.fromEntries([...gradients].flatMap((name) => theme.gradients[name] ? [[name, theme.gradients[name]]] : [])),
    },
    mediaUrls,
    assetIds,
    campaignIds,
  };
}

function rewriteNodeTokens(
  node: AuthoringBlockNode,
  colors: ReadonlyMap<string, string>,
  fonts: ReadonlyMap<string, string>,
  buttons: ReadonlyMap<string, string>,
  radii: ReadonlyMap<string, string>,
  elevations: ReadonlyMap<string, string>,
  gradients: ReadonlyMap<string, string>
): void {
  const color = (value: string): string => colors.get(value) ?? value;
  const style = node.style;
  if (style?.bg) {
    if (typeof style.bg === 'string') style.bg = color(style.bg);
    else if ('gradient' in style.bg) style.bg = { gradient: gradients.get(style.bg.gradient) ?? style.bg.gradient };
    else style.bg = { ...style.bg, stops: style.bg.stops.map(color) };
  }
  if (style?.border) style.border.color = color(style.border.color);
  if (style?.shadow) {
    style.shadow = typeof style.shadow === 'string' ? (elevations.get(style.shadow) ?? style.shadow) : { ...style.shadow, color: color(style.shadow.color) };
  }
  if (typeof style?.radius === 'string') style.radius = radii.get(style.radius) ?? style.radius;
  switch (node.block) {
    case 'text': node.font = fonts.get(node.font) ?? node.font; node.color = color(node.color); break;
    case 'button': node.font = fonts.get(node.font) ?? node.font; node.variant = buttons.get(node.variant) ?? node.variant; break;
    case 'image': if (node.tint) node.tint = color(node.tint); if (typeof node.radius === 'string') node.radius = radii.get(node.radius) ?? node.radius; break;
    case 'video': if (typeof node.radius === 'string') node.radius = radii.get(node.radius) ?? node.radius; break;
    case 'progress': node.fill_color = color(node.fill_color); node.track_color = color(node.track_color); break;
    default: break;
  }
}
